import errorImage from "@assets/errors/error-screen.svg";
import ArrowBackLight from "@assets/navigation/back.svg";
import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import TransactionStatusClass from "@components/classes/TransactionStatusClass";
import GlobalSearchInputField from "@components/inputs/GlobalSearchInputField";
import DataPill from "@components/pills/DataPill";
import Switcher from "@components/switcher/Switcher";
import { userManagementApis } from "@services/apis/UserManagement";
import {
  UsersRequest,
  UsersResponseData,
} from "@services/models/UserManagement/users";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import ConstantFunctions from "../../../utils/constants_functions";
import TransactionsContainer from "../../user_management/wallet/TransactionContainer";
import Account from "../../user_management/Account";
import Kyckyb from "../../user_management/KycKyb";
import Services from "../../user_management/Services";
import Wallet from "../../user_management/Wallet";

const ROWS_PER_PAGE = 100;

const MobileUserManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const transactionStatusClass = new TransactionStatusClass();
  const constantFunction = new ConstantFunctions();
  const individualGridRef = useRef<any>(null);
  const merchantGridRef = useRef<any>(null);
  const [error, setError] = useState("");
  const [individualSearchTerm, setIndividualSearchTerm] = useState("");
  const [appliedIndividualSearch, setAppliedIndividualSearch] = useState("");
  const [merchantSearchTerm, setMerchantSearchTerm] = useState("");
  const [appliedMerchantSearch, setAppliedMerchantSearch] = useState("");
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [, setIsIndividual] = useState<boolean | null>(null);
  const [, setIsPanelCollapsed] = useState<boolean>(false);
  const [showDetailView, setShowDetailView] = useState<boolean>(false);

  const individualFetchingRef = useRef(false);
  const merchantFetchingRef = useRef(false);
  const individualRequestIdRef = useRef(0);
  const merchantRequestIdRef = useRef(0);
  const individualPageRef = useRef(1);
  const merchantPageRef = useRef(1);
  const individualHasMoreRef = useRef(true);
  const merchantHasMoreRef = useRef(true);
  const appliedIndividualSearchRef = useRef("");
  const appliedMerchantSearchRef = useRef("");
  const didInitialFetch = useRef(false);

  const [individualUsers, setIndividualUsers] = useState<UsersResponseData[]>(
    [],
  );
  const [individualLoading, setIndividualLoading] = useState(false);

  const [merchantUsers, setMerchantUsers] = useState<UsersResponseData[]>([]);
  const [merchantLoading, setMerchantLoading] = useState(false);

  const [isUserProfileMissing, setIsUserProfileMissing] = useState(false);

  const getSelectedUserAccountType = (): string => {
    if (!selectedUserId) return "";

    const individualUser = individualUsers.find(
      (user) => user.userId === selectedUserId,
    );
    if (individualUser) return individualUser.accountType;

    const merchantUser = merchantUsers.find(
      (user) => user.userId === selectedUserId,
    );
    if (merchantUser) return merchantUser.accountType;

    return "";
  };

  const fetchUserProfile = async ({
    userId,
    accountType,
  }: {
    userId: number;
    accountType: string;
  }) => {
    try {
      setIsUserProfileMissing(false);

      let type =
        accountType === "institutional" || accountType === "small_merchant"
          ? "merchant"
          : "individual";

      await userManagementApis.getUserDetails(userId, type);
    } catch (error: any) {
      const message = error?.response?.data?.msg;

      if (error?.response?.status === 404 && message === "No user found") {
        setIsUserProfileMissing(true);
        return;
      }

      setError("Failed to fetch user profile");
    }
  };

  const tabs = [
    {
      label: "ACCOUNT",
      component: (
        <Account
          userId={selectedUserId!}
          accountType={getSelectedUserAccountType()}
        />
      ),
    },
    {
      label: "SERVICES",
      component: (
        <div className="mobile-hide-sidebar">
          <Services
            userId={selectedUserId!}
            accountType={getSelectedUserAccountType()}
          />
        </div>
      ),
    },
    {
      label: "WALLET",
      component: (
        <div className="mobile-hide-sidebar">
          <Wallet
            userId={selectedUserId!}
            accountType={getSelectedUserAccountType()}
          />
        </div>
      ),
    },
    {
      label: getSelectedUserAccountType() === "institutional" ? "KYB" : "KYC",
      component: (
        <Kyckyb
          userId={selectedUserId!}
          accountType={getSelectedUserAccountType()}
        />
      ),
    },
  ];

  const selectFirstIndividualUser = (users: UsersResponseData[]) => {
    if (users.length === 0) return;

    const firstUser = users[0];
    setSelectedUserId(firstUser.userId);
    setIsIndividual(true);
    setIsPanelCollapsed(false);

    setTimeout(() => {
      const gridApi = individualGridRef.current?.api;
      if (gridApi) {
        const firstNode = gridApi.getDisplayedRowAtIndex(0);
        if (firstNode) {
          firstNode.setSelected(true);

          const rowElement = document.querySelector(
            `[row-id="${firstNode.id}"]`,
          );

          if (rowElement) {
            document
              .querySelectorAll(".ag-row")
              .forEach((r) => r.classList.remove("ag-row-selected-custom"));

            rowElement.classList.add("ag-row-selected-custom");
          }
        }
      }
    }, 50);
  };

  const fetchIndividualUsers = async (
    pageNumber: number = 1,
    reset: boolean = false,
    search: string = appliedIndividualSearch,
  ) => {
    const requestId = reset
      ? ++individualRequestIdRef.current
      : individualRequestIdRef.current;

    individualFetchingRef.current = true;
    setIndividualLoading(true);
    setError("");
    if (reset) {
      setIndividualUsers([]);
    }

    try {
      const request: UsersRequest = {
        page: pageNumber,
        limit: ROWS_PER_PAGE,
        type: "individual",
        ...(search ? { search } : {}),
      };

      const response = await userManagementApis.getUsers(request);
      if (requestId !== individualRequestIdRef.current) return;

      const newData = response.data || [];

      if (reset) {
        setIndividualUsers(newData);
      } else {
        setIndividualUsers((prev) => [...prev, ...newData]);
      }

      individualHasMoreRef.current = newData.length >= ROWS_PER_PAGE;

      if (reset) {
        selectFirstIndividualUser(newData);
      }
    } catch (error) {
      if (requestId !== individualRequestIdRef.current) return;
      setError("Failed to fetch users.");
    } finally {
      if (requestId === individualRequestIdRef.current) {
        setIndividualLoading(false);
        individualFetchingRef.current = false;
      }
    }
  };

  const fetchMerchantUsers = async (
    pageNumber: number = 1,
    reset: boolean = false,
    search: string = appliedMerchantSearch,
  ) => {
    const requestId = reset
      ? ++merchantRequestIdRef.current
      : merchantRequestIdRef.current;

    merchantFetchingRef.current = true;
    setMerchantLoading(true);
    setError("");
    if (reset) {
      setMerchantUsers([]);
    }

    try {
      const request: UsersRequest = {
        page: pageNumber,
        limit: ROWS_PER_PAGE,
        type: "merchant",
        ...(search ? { search } : {}),
      };

      const response = await userManagementApis.getUsers(request);
      if (requestId !== merchantRequestIdRef.current) return;

      const newData = response.data || [];

      if (reset) {
        setMerchantUsers(newData);
      } else {
        setMerchantUsers((prev) => [...prev, ...newData]);
      }

      merchantHasMoreRef.current = newData.length >= ROWS_PER_PAGE;
    } catch (error) {
      if (requestId !== merchantRequestIdRef.current) return;
      setError("Failed to fetch users.");
    } finally {
      if (requestId === merchantRequestIdRef.current) {
        setMerchantLoading(false);
        merchantFetchingRef.current = false;
      }
    }
  };

  const handleTabChange = (index: number) => {
    setActiveTabIndex(index);
  };

  const individualColumnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "UID",
        field: "userId",
        minWidth: 70,
        flex: 1,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <span>{data.userId}</span>
            <CopyButton
              copyText={data.userId}
              copyMessage="User ID copied!"
              buttonSize="14px"
              copyIconSize={12}
            />
          </div>
        ),
      },
      {
        headerName: "Email ID",
        field: "emailId",
        minWidth: 140,
        flex: 2,
        cellRenderer: ({ data }: any) => {
          const value = data?.emailId ? data.emailId.toString() : "";

          return (
            <div className="flex items-center h-full w-full gap-2">
              <div className="flex flex-col leading-[14px] flex-1 min-w-0">
                {value ? (
                  Array.from(
                    { length: Math.ceil(value.length / 14) },
                    (_, index) => (
                      <span
                        key={index}
                        className="text-[10px] text-text-base break-all"
                        title={value}>
                        {value.slice(index * 14, (index + 1) * 14)}
                      </span>
                    ),
                  )
                ) : (
                  <span className="text-[10px] text-text-base">- -</span>
                )}
              </div>

              {value && (
                <div className="flex-shrink-0">
                  <CopyButton
                    copyText={value}
                    copyMessage="Email ID copied!"
                    buttonSize="14px"
                    copyIconSize={12}
                  />
                </div>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Category",
        field: "accountType",
        minWidth: 100,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <DataPill value={data?.accountType} />
        ),
      },
      {
        headerName: "KYC Status",
        minWidth: 100,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => {
          const bgColor =
            data.overallKycStatus &&
            transactionStatusClass.getTransactionBgColor(data.overallKycStatus);

          const textColor =
            data.overallKycStatus &&
            transactionStatusClass.getTransactionIconColor(
              data.overallKycStatus,
            );

          return (
            <span
              className={`inline-flex h-[20px] items-center justify-center gap-1 whitespace-nowrap rounded px-[6px] text-[10px] font-medium ${bgColor}`}
              style={{ color: textColor }}>
              {data.overallKycStatus?.toUpperCase() || "-"}
            </span>
          );
        },
      },
      {
        headerName: "USDT",
        field: "usdt_balance",
        minWidth: 90,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <img
              src={"https://assets.tylt.money/holdings-icons/USDT.png"}
              alt={"USDT"}
              className="w-[12px] h-[12px] rounded"
            />
            <span>{constantFunction.formatAmount(data.usdt_balance)}</span>
          </div>
        ),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "USDC",
        minWidth: 90,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <img
              src={"https://assets.tylt.money/holdings-icons/USDC.png"}
              alt={"USDC"}
              className="w-[12px] h-[12px] rounded"
            />
            <span>{constantFunction.formatAmount(data.usdc_balance)}</span>
          </div>
        ),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "DAI",
        field: "dai_balance",
        minWidth: 90,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <img
              src={"https://assets.tylt.money/holdings-icons/DAI.png"}
              alt={"DAI"}
              className="w-[12px] h-[12px] rounded"
            />
            <span>{constantFunction.formatAmount(data.dai_balance)}</span>
          </div>
        ),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Total",
        field: "total_balance",
        minWidth: 100,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <span>${constantFunction.formatAmount(data.total_balance)}</span>
          </div>
        ),
        filter: "agNumberColumnFilter",
      },
    ],
    [],
  );

  const merchantColumnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "UID",
        field: "userId",
        minWidth: 70,
        flex: 1,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <span>{data.userId}</span>
            <CopyButton
              copyText={data.userId}
              copyMessage="User ID copied!"
              buttonSize="14px"
              copyIconSize={12}
            />
          </div>
        ),
      },
      {
        headerName: "Email ID",
        field: "emailId",
        minWidth: 140,
        flex: 2,
        cellRenderer: ({ data }: any) => {
          const value = data?.emailId ? data.emailId.toString() : "";

          return (
            <div className="flex items-center h-full w-full gap-2">
              <div className="flex flex-col leading-[14px] flex-1 min-w-0">
                {value ? (
                  Array.from(
                    { length: Math.ceil(value.length / 14) },
                    (_, index) => (
                      <span
                        key={index}
                        className="text-[10px] text-text-base break-all"
                        title={value}>
                        {value.slice(index * 14, (index + 1) * 14)}
                      </span>
                    ),
                  )
                ) : (
                  <span className="text-[10px] text-text-base">- -</span>
                )}
              </div>

              {value && (
                <div className="flex-shrink-0">
                  <CopyButton
                    copyText={value}
                    copyMessage="Email ID copied!"
                    buttonSize="14px"
                    copyIconSize={12}
                  />
                </div>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Category",
        field: "accountType",
        minWidth: 130,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <DataPill value={data?.accountType} />
        ),
      },
      {
        headerName: "Submitted",
        minWidth: 80,
        flex: 1,
        sortable: true,
        cellClass: "flex items-center justify-center",
        cellRenderer: ({ data }: any) => {
          const kybStatus = data.isSubmitted ? "YES" : "NO";

          const bgColor = transactionStatusClass.getTransactionBgColor(
            kybStatus.toLowerCase(),
          );

          const textColor = transactionStatusClass.getTransactionIconColor(
            kybStatus.toLowerCase(),
          );

          return (
            <span
              className={`h-[20px] flex items-center justify-center rounded text-[10px] font-medium px-[10px] ${bgColor}`}
              style={{ color: textColor }}>
              {kybStatus}
            </span>
          );
        },
      },
      {
        headerName: "USDT",
        field: "usdt_balance",
        minWidth: 90,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <img
              src={"https://assets.tylt.money/holdings-icons/USDT.png"}
              alt={"USDT"}
              className="w-[12px] h-[12px] rounded"
            />
            <span>{constantFunction.formatAmount(data.usdt_balance)}</span>
          </div>
        ),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "USDC",
        minWidth: 90,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <img
              src={"https://assets.tylt.money/holdings-icons/USDC.png"}
              alt={"USDC"}
              className="w-[12px] h-[12px] rounded"
            />
            <span>{constantFunction.formatAmount(data.usdc_balance)}</span>
          </div>
        ),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "DAI",
        field: "dai_balance",
        minWidth: 90,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <img
              src={"https://assets.tylt.money/holdings-icons/DAI.png"}
              alt={"DAI"}
              className="w-[12px] h-[12px] rounded"
            />
            <span>{constantFunction.formatAmount(data.dai_balance)}</span>
          </div>
        ),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Total",
        field: "total_balance",
        minWidth: 100,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <span>${constantFunction.formatAmount(data.total_balance)}</span>
          </div>
        ),
        filter: "agNumberColumnFilter",
      },
    ],
    [],
  );

  const onRowClicked = (event: RowClickedEvent, isIndividualGrid: boolean) => {
    const allRows = document.querySelectorAll(".ag-row");
    allRows.forEach((row) => {
      row.classList.remove("ag-row-selected-custom");
    });

    if (event.rowIndex !== null) {
      const gridElement =
        (event.api as any).gridCore?.eGridDiv || (event.api as any).eGridDiv;

      if (gridElement) {
        const rowElement = gridElement.querySelector(
          `[row-index="${event.rowIndex}"]`,
        );
        if (rowElement) {
          rowElement.classList.add("ag-row-selected-custom");
        }
      } else {
        const rowNode = event.node;
        if (rowNode) {
          const rowElement = document.querySelector(`[row-id="${rowNode.id}"]`);
          if (rowElement) {
            rowElement.classList.add("ag-row-selected-custom");
          }
        }
      }
    }
    setSelectedUserId(event.data.userId);
    setIsIndividual(isIndividualGrid);
    setIsPanelCollapsed(false);
    setIsUserProfileMissing(false);
    setShowDetailView(true);
    setActiveTabIndex(0);

    fetchUserProfile({
      userId: event.data.userId,
      accountType: event.data.accountType,
    });
  };

  const handleIndividualScrollEnd = (params: any) => {
    if (!individualHasMoreRef.current || individualFetchingRef.current) {
      return;
    }

    const api = params.api;
    const displayedRowCount = api.getDisplayedRowCount();
    const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

    if (
      lastDisplayedRowIndex >= displayedRowCount - 5 &&
      displayedRowCount > 0
    ) {
      individualFetchingRef.current = true;
      individualPageRef.current += 1;
      fetchIndividualUsers(
        individualPageRef.current,
        false,
        appliedIndividualSearchRef.current,
      );
    }
  };

  const handleMerchantScrollEnd = (params: any) => {
    if (!merchantHasMoreRef.current || merchantFetchingRef.current) {
      return;
    }

    const api = params.api;
    const displayedRowCount = api.getDisplayedRowCount();
    const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

    if (
      lastDisplayedRowIndex >= displayedRowCount - 5 &&
      displayedRowCount > 0
    ) {
      merchantFetchingRef.current = true;
      merchantPageRef.current += 1;
      fetchMerchantUsers(
        merchantPageRef.current,
        false,
        appliedMerchantSearchRef.current,
      );
    }
  };

  const handleIndividualSearchSubmit = () => {
    const value = individualSearchTerm.trim();
    if (!value) return;

    setSelectedUserId(null);
    setShowDetailView(false);
    appliedIndividualSearchRef.current = value;
    individualPageRef.current = 1;
    individualHasMoreRef.current = true;
    setAppliedIndividualSearch(value);
    fetchIndividualUsers(1, true, value);
  };

  const clearIndividualSearch = () => {
    setIndividualSearchTerm("");
    appliedIndividualSearchRef.current = "";
    individualPageRef.current = 1;
    individualHasMoreRef.current = true;
    setAppliedIndividualSearch("");
    setSelectedUserId(null);
    setShowDetailView(false);
    fetchIndividualUsers(1, true, "");
  };

  const handleMerchantSearchSubmit = () => {
    const value = merchantSearchTerm.trim();
    if (!value) return;

    appliedMerchantSearchRef.current = value;
    merchantPageRef.current = 1;
    merchantHasMoreRef.current = true;
    setAppliedMerchantSearch(value);
    fetchMerchantUsers(1, true, value);
  };

  const clearMerchantSearch = () => {
    setMerchantSearchTerm("");
    appliedMerchantSearchRef.current = "";
    merchantPageRef.current = 1;
    merchantHasMoreRef.current = true;
    setAppliedMerchantSearch("");
    fetchMerchantUsers(1, true, "");
  };

  useEffect(() => {
    if (didInitialFetch.current) return;
    didInitialFetch.current = true;
    fetchIndividualUsers(1, true, "");
    fetchMerchantUsers(1, true, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isInitialIndividualLoading =
    individualLoading && individualUsers.length === 0;
  const isInitialMerchantLoading =
    merchantLoading && merchantUsers.length === 0;

  const pathParts = location.pathname.split("/").filter(Boolean);
  const isTransactionRoute =
    pathParts.length === 2 && !isNaN(Number(pathParts[0]));

  if (isTransactionRoute) {
    const txnId = parseInt(pathParts[0], 10);
    const ledger = pathParts[1];
    const routeState = (location.state || {}) as any;

    return (
      <div className="flex flex-col h-full">
        <div className="h-[56px] border-b border-border-subtle bg-bg-page-body flex flex-row items-center px-4 shrink-0 gap-3">
          <img
            src={ArrowBackLight}
            alt="Back"
            className="cursor-pointer"
            onClick={() => navigate("/")}
          />
          <p className="text-text-base text-[14px] font-semibold">
            Transaction Details
          </p>
        </div>
        <div className="flex-1 overflow-auto min-h-0">
          <TransactionsContainer
            transactionID={txnId}
            ledger={ledger}
            userId={routeState?.userId || 0}
            merchantId={routeState?.merchantId || 0}
          />
        </div>
      </div>
    );
  }

  if (showDetailView && selectedUserId) {
    return (
      <div className="flex flex-col h-full">
        <div className="h-[56px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 px-4 w-full shrink-0">
          <div className="flex gap-2 items-center w-full">
            <img
              src={ArrowBackLight}
              alt="Back"
              className="cursor-pointer"
              onClick={() => setShowDetailView(false)}
            />
            <p className="flex text-text-base text-[13px] font-semibold items-center">
              User Details (#{selectedUserId})
            </p>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-3">
          {isUserProfileMissing ? (
            <div className="flex flex-col items-center justify-center rounded-[12px] bg-bg-page-body py-[24px]">
              <img
                src={errorImage}
                alt="Error Illustration"
                className="w-[200px] h-auto max-h-[200px] object-contain"
              />
              <p className="text-[14px] font-medium text-text-base mt-[16px]">
                User profile doesn't exist!!
              </p>
            </div>
          ) : (
            <Switcher
              tabs={tabs}
              activeTabIndex={activeTabIndex}
              onTabChange={handleTabChange}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col overflow-auto">
      <div className="min-h-[56px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 px-[16px] w-full">
        <div className="flex flex-grow justify-between items-center w-full">
          <p className="flex text-text-base text-[14px] font-semibold items-center">
            Users
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-0 items-start flex-1 overflow-auto">
        <div className="flex flex-col w-full px-[12px] pt-[12px]">
          <div className="flex flex-col gap-[8px] mb-[8px]">
            <span className="text-text-base font-semibold text-[12px]">
              Individual
            </span>
            <div className="w-full">
              <GlobalSearchInputField
                value={individualSearchTerm}
                onChange={(e) => setIndividualSearchTerm(e.target.value)}
                onSearch={handleIndividualSearchSubmit}
                onClear={clearIndividualSearch}
                disableSearchButton={!individualSearchTerm.trim()}
                isSearchApplied={Boolean(appliedIndividualSearch)}
                placeholder="Search by User ID / Email ID"
              />
            </div>
          </div>

          <div
            className="bg-bg-page-body overflow-x-auto relative"
            style={{ height: "300px" }}
          >
            {isInitialIndividualLoading ? (
              <AgGridSkeleton
                columnDefs={individualColumnDefs}
                style={{ height: "100%", minWidth: "700px", width: "100%" }}
              />
            ) : (
              <div
                className="ag-theme-alpine ag-grid-12px h-full"
                style={{ minWidth: "700px" }}
              >
                <AgGridReactTable
                  ref={individualGridRef}
                  onRowClicked={(event) => onRowClicked(event, true)}
                  onBodyScroll={handleIndividualScrollEnd}
                  rowData={individualUsers}
                  columnDefs={individualColumnDefs}
                  error={error}
                  getRowId={(params) => params.data.userId.toString()}
                />
              </div>
            )}
          </div>
          {individualLoading && individualUsers.length > 0 && (
            <div className="flex items-center justify-center py-2 border-t border-border-subtle">
              <div className="flex items-center space-x-2 text-text-subtle">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-border-subtle border-t-border-button-primary"></div>
                <span className="text-sm">Loading more users...</span>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-[8px] my-[8px]">
            <span className="text-text-base font-semibold text-[12px]">
              Merchant
            </span>
            <div className="w-full">
              <GlobalSearchInputField
                value={merchantSearchTerm}
                onChange={(e) => setMerchantSearchTerm(e.target.value)}
                onSearch={handleMerchantSearchSubmit}
                onClear={clearMerchantSearch}
                disableSearchButton={!merchantSearchTerm.trim()}
                isSearchApplied={Boolean(appliedMerchantSearch)}
                placeholder="Search by User ID / Email ID"
              />
            </div>
          </div>

          <div
            className="bg-bg-page-body overflow-x-auto relative"
            style={{ height: "300px" }}
          >
            {isInitialMerchantLoading ? (
              <AgGridSkeleton
                columnDefs={merchantColumnDefs}
                style={{ height: "100%", minWidth: "700px", width: "100%" }}
              />
            ) : (
              <div className="h-full" style={{ minWidth: "700px" }}>
                <AgGridReactTable
                  ref={merchantGridRef}
                  rowData={merchantUsers}
                  onRowClicked={(event) => onRowClicked(event, false)}
                  onBodyScroll={handleMerchantScrollEnd}
                  columnDefs={merchantColumnDefs}
                  error={error}
                  getRowId={(params) => params.data.userId.toString()}
                />
              </div>
            )}
          </div>
          {merchantLoading && merchantUsers.length > 0 && (
            <div className="flex items-center justify-center py-2 border-t border-border-subtle">
              <div className="flex items-center space-x-2 text-text-subtle">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-border-subtle border-t-border-button-primary"></div>
                <span className="text-sm">Loading more users...</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <Outlet />
    </div>
  );
};

export default MobileUserManagement;
